/**
 * One big pickable card in a connect fork — used both for choosing which app to
 * connect (WhatsApp vs Signal) and for choosing how to connect it (QR / shared
 * number / import).
 *
 * `comingSoon` renders a non-interactive card rather than hiding the option, so
 * the fork still tells the whole story. It is an `aria-disabled` div rather than
 * a disabled button so keyboard and screen-reader users get the same signal
 * sighted users do.
 *
 * `badge` is independent of `comingSoon`: an option can be reachable and still
 * carry a caveat ("Experimental"), which is not the same claim as "you can't
 * have it yet".
 */
import type { LucideIcon } from 'lucide-react';
import { IconTile } from '@/components/patterns/icon-tile';

export interface ConnectOptionProps {
  icon: LucideIcon;
  tone?: 'green' | 'neutral';
  title: string;
  body: string;
  onClick?: () => void;
  comingSoon?: boolean;
  /**
   * Chip text next to the title. Defaults to "Coming soon" on a `comingSoon`
   * card; on a clickable one it shows only when set (e.g. "Experimental").
   */
  badge?: string;
}

export function ConnectOption({
  icon: Icon,
  tone = 'neutral',
  title,
  body,
  onClick,
  comingSoon,
  badge,
}: ConnectOptionProps) {
  const content = (
    <div className="flex items-start gap-4">
      <IconTile tone={comingSoon ? 'neutral' : tone}>
        <Icon className="h-5 w-5" />
      </IconTile>
      <div>
        <h3 className="flex items-center gap-2 font-semibold text-[15px]">
          {title}
          {(comingSoon || badge) && (
            <span className="rounded-full border border-border-subtle bg-muted px-2 py-0.5 font-medium text-[11px] text-faint">
              {badge ?? 'Coming soon'}
            </span>
          )}
        </h3>
        <p className="mt-1 text-[13.5px] text-muted-foreground">{body}</p>
      </div>
    </div>
  );

  if (comingSoon) {
    return (
      <div
        aria-disabled="true"
        className="block w-full cursor-not-allowed rounded-xl border border-border p-6 text-left opacity-60"
      >
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full rounded-xl border border-border p-6 text-left transition-colors hover:border-foreground/30"
    >
      {content}
    </button>
  );
}
